/**
 * #1630 — theme activation resolution.
 *
 * Pure function shared conceptually with the blocking inline script in
 * index.html (which can't import a module and re-implements this same
 * three-line rule in vanilla JS — keep the two in sync if this changes)
 * and ThemeContext.jsx (which imports it directly).
 *
 * Decision log (Gate-1, 2026-07-02): "Activation: follow the device
 * (prefers-color-scheme) with a manual override persisted per learner."
 *
 * INTERIM SHIP-GATE (mega60 human-test, 2026-07-04): system-following is
 * honored only for an EXPLICIT stored 'system' choice (the Match-device
 * option). A never-touched/unset/corrupted value defaults to LIGHT — the
 * Gate-2 test surfaced un-QA'd dark surfaces (org-dashboard, testing
 * panel, likely more), so auto-dark for dark-OS users would ship broken
 * pages by default. Restore "unset follows the device" once the dark-mode
 * QA sweep issue closes.
 */

export const THEME_STORAGE_KEY = 'tacit-theme';

/**
 * HIDDEN FOR LAUNCH (#4194) — the dark-mode master switch.
 *
 * While false the app is light everywhere: resolveTheme() returns 'light' for
 * every input, ThemeContext's single writer (applyTheme) coerces to 'light',
 * the guest discovery pages do not follow the OS, and the Settings Theme row
 * is not rendered. The [data-theme='dark'] token blocks stay in the CSS (inert
 * while nothing sets the attribute) and any stored `tacit-theme` is left
 * untouched, so the dark-mode overhaul re-enables everything by flipping this
 * one constant (plus the matching pre-mount script in frontend/index.html,
 * which cannot import this module — see the comment above it).
 */
export const DARK_MODE_ENABLED = false;

/**
 * @param {string|null|undefined} stored - raw localStorage value
 * @param {boolean} prefersDark - matchMedia('(prefers-color-scheme: dark)').matches
 * @param {{ darkEnabled?: boolean }} [options] - defaults to DARK_MODE_ENABLED
 * @returns {'light'|'dark'}
 */
export function resolveTheme(stored, prefersDark, { darkEnabled = DARK_MODE_ENABLED } = {}) {
  if (!darkEnabled) return 'light';
  if (stored === 'light' || stored === 'dark') return stored;
  if (stored === 'system') return prefersDark ? 'dark' : 'light';
  return 'light';
}

/** True when `stored` is an explicit, recognized override (not system-following). */
export function isExplicitTheme(stored) {
  return stored === 'light' || stored === 'dark';
}
